import { Injectable, signal, computed, inject, NgZone, OnDestroy } from '@angular/core';
import { environment } from '@/environments/environment';

export type PermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

export interface LocationCoordinates {
  lat: number;
  lng: number;
  accuracy?: number;
}

export interface LocationResult {
  success: boolean;
  coordinates?: LocationCoordinates;
  error?: string;
}

/** 1 km proximity rule for entries. */
export const PROXIMITY_LIMIT_M = 1000;
/**
 * Maximum benefit-of-the-doubt tolerance granted by the GPS uncertainty
 * circle when evaluating the proximity rule. Prevents arbitrarily large
 * accuracies from bypassing the rule.
 */
export const ACCURACY_TOLERANCE_CAP_M = 500;
/**
 * When the reported accuracy exceeds this, the fix is considered too
 * unreliable to evaluate the proximity rule at all.
 */
export const ACCURACY_HARD_LIMIT_M = 1000;

export type ProximityStatus = 'ok' | 'too-far' | 'low-accuracy' | 'no-location';

export interface ProximityResult {
  status: ProximityStatus;
  distanceM: number | null;
  accuracyM: number | null;
}

/**
 * Format an accuracy value in meters to a human-readable string.
 * Returns "25 m" below 1000 m, otherwise kilometers with one decimal
 * ("1.2 km"). Null or undefined input returns "—".
 */
export function formatAccuracy(accuracyM: number | null | undefined): string {
  if (accuracyM == null || !isFinite(accuracyM)) return '—';
  if (accuracyM < 1000) return `${Math.round(accuracyM)} m`;
  return `${(accuracyM / 1000).toFixed(1)} km`;
}

/**
 * Haversine great-circle distance in meters between two lat/lng points.
 * Shared by all proximity checks in the app.
 */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371e3;
  const phi1 = (a.lat * Math.PI) / 180;
  const phi2 = (b.lat * Math.PI) / 180;
  const dPhi = ((b.lat - a.lat) * Math.PI) / 180;
  const dLambda = ((b.lng - a.lng) * Math.PI) / 180;

  const s =
    Math.sin(dPhi / 2) * Math.sin(dPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) * Math.sin(dLambda / 2);
  const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  return R * c;
}

@Injectable({
  providedIn: 'root',
})
export class LocationPermissionService implements OnDestroy {
  private readonly ngZone = inject(NgZone);
  private permissionStatus: PermissionStatus | null = null;
  private changeListeners: ((state: PermissionState) => void)[] = [];

  private readonly UPDATE_THROTTLE_MS = 300;
  private watchId: number | null = null;
  private lastEmitAt = 0;
  private visibilityHandler?: () => void;

  private readonly _permissionState = signal<PermissionState>('unknown');
  private readonly _wasExplicitlyDenied = signal<boolean>(false);
  private readonly _showDialog = signal<boolean>(false);
  private readonly _currentLocation = signal<LocationCoordinates | null>(null);
  private readonly _locationError = signal<string>('');

  readonly permissionState = computed(() => this._permissionState());
  readonly wasExplicitlyDenied = computed(() => this._wasExplicitlyDenied());
  readonly isPermissionDenied = computed(() => 
    this._permissionState() === 'denied' || this._wasExplicitlyDenied()
  );
  
  readonly showDialog = computed(() => this._showDialog());
  readonly currentLocation = computed(() => this._currentLocation());
  readonly locationError = computed(() => this._locationError());
  readonly hasLocation = computed(() => !!this._currentLocation());
  readonly currentAccuracy = computed(() => this._currentLocation()?.accuracy ?? null);
  readonly isAccuracyReliable = computed(() => {
    const a = this.currentAccuracy();
    return a !== null && a <= ACCURACY_HARD_LIMIT_M;
  });
  readonly helpUrl = environment.locationPermissionHelpUrl;

  constructor() {
    this.initializePermissionListener();
  }

  ngOnDestroy(): void {
    this.stopTracking();
    this.cleanup();
  }

  startTracking(): void {
    if (this.watchId !== null) return;
    if (!navigator.geolocation) return;
    if (this._permissionState() !== 'granted') return;

    this.beginWatch();

    if (typeof document !== 'undefined') {
      this.visibilityHandler = () => {
        if (document.hidden) {
          this.stopWatch();
        } else if (this._permissionState() === 'granted') {
          this.beginWatch();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  stopTracking(): void {
    this.stopWatch();
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
  }

  private beginWatch(): void {
    if (this.watchId !== null || !navigator.geolocation) return;

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        const now = performance.now();
        if (now - this.lastEmitAt < this.UPDATE_THROTTLE_MS) return;
        this.lastEmitAt = now;
        this.ngZone.run(() => {
          this._currentLocation.set({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
          this._locationError.set('');
        });
      },
      () => {
        // Silent background watch: keep the last fix on failure.
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 0 }
    );
  }

  private stopWatch(): void {
    if (this.watchId !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(this.watchId);
    }
    this.watchId = null;
    // Note: keep `lastEmitAt` so freshness checks still know how old the
    // last accepted fix is when the watch resumes.
  }

  openDialog(): void {
    this._showDialog.set(true);
  }

  closeDialog(): void {
    this._showDialog.set(false);
  }

  async requestLocation(showDialogOnError = false): Promise<LocationResult> {
    this._locationError.set('');

    if (!navigator.geolocation) {
      this.ngZone.run(() => {
        this._locationError.set('errors.gps_unavailable');
        if (showDialogOnError) {
          this._showDialog.set(true);
        }
      });
      return { success: false, error: 'errors.gps_unavailable' };
    }

    // Check permission state first - if denied at browser level, show dialog
    if (navigator.permissions) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
        if (permissionStatus.state === 'denied') {
          this.ngZone.run(() => {
            this._locationError.set('errors.location_permission_denied');
            if (showDialogOnError) {
              this._showDialog.set(true);
            }
          });
          return { success: false, error: 'errors.location_permission_denied' };
        }
      } catch {
        // Permissions API not supported, continue with geolocation request
      }
    }

    // The watchPosition stream from startTracking() already keeps a fresh
    // fix in `_currentLocation`. If we have one and permission is granted,
    // hand it back immediately and let the stream keep it updated. This
    // prevents user-visible "Unable to get your location" errors on slow
    // GPS locks when we already have a perfectly good streamed reading.
    const existing = this._currentLocation();
    if (existing && this._permissionState() === 'granted') {
      this.ngZone.run(() => {
        this._locationError.set('');
        this._showDialog.set(false);
        this.clearDeniedState();
      });
      this.startTracking();
      return { success: true, coordinates: existing };
    }

    return new Promise((resolve) => {
      const tryGetPosition = (attempt: number) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords: LocationCoordinates = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              accuracy: position.coords.accuracy,
            };
            this.lastEmitAt = performance.now();
            this.ngZone.run(() => {
              this._currentLocation.set(coords);
              this._locationError.set('');
              this._showDialog.set(false);
              this.clearDeniedState();
              // We just got a position, so permission is effectively granted
              // even if the Permissions API hasn't reported it yet. This
              // unlocks startTracking() below on first run.
              this._permissionState.set('granted');
            });
            this.startTracking();
            resolve({ success: true, coordinates: coords });
          },
          (error) => {
            if (error.code === 1) {
              this.ngZone.run(() => {
                this.markAsExplicitlyDenied();
                this._locationError.set('errors.location_permission_denied');
                if (showDialogOnError) {
                  this._showDialog.set(true);
                }
              });
              resolve({ success: false, error: 'errors.location_permission_denied' });
              return;
            }

            // Retry once on transient failures (POSITION_UNAVAILABLE or
            // TIMEOUT). High-accuracy GPS can take a while to lock,
            // especially the first time after the page loads.
            if ((error.code === 2 || error.code === 3) && attempt === 0) {
              tryGetPosition(1);
              return;
            }

            // Final failure. If the watchPosition stream produced a fix
            // in the meantime, return it instead of surfacing an error.
            const fallback = this._currentLocation();
            if (fallback) {
              this.ngZone.run(() => {
                this._locationError.set('');
                this._showDialog.set(false);
                this.clearDeniedState();
              });
              this.startTracking();
              resolve({ success: true, coordinates: fallback });
              return;
            }

            this.ngZone.run(() => {
              this._locationError.set('map.location_error');
              if (showDialogOnError) {
                this._showDialog.set(true);
              }
            });
            resolve({ success: false, error: 'map.location_error' });
          },
          {
            enableHighAccuracy: true,
            timeout: attempt === 0 ? 15_000 : 20_000,
            maximumAge: 0,
          }
        );
      };
      tryGetPosition(0);
    });
  }

  /**
   * Accuracy-aware proximity evaluator. Returns a structured result so
   * callers can differentiate "too far" from "GPS too imprecise to judge".
   */
  evaluateProximity(target: { lat: number; lng: number }): ProximityResult {
    const user = this._currentLocation();
    if (!user || user.accuracy == null) {
      return { status: 'no-location', distanceM: null, accuracyM: null };
    }
    const accuracy = user.accuracy;
    if (accuracy > ACCURACY_HARD_LIMIT_M) {
      return { status: 'low-accuracy', distanceM: null, accuracyM: accuracy };
    }
    const distanceM = haversineMeters(user, target);
    const tolerance = Math.min(accuracy, ACCURACY_TOLERANCE_CAP_M);
    return {
      status: distanceM - tolerance > PROXIMITY_LIMIT_M ? 'too-far' : 'ok',
      distanceM,
      accuracyM: accuracy,
    };
  }

  /**
   * Default freshness thresholds for `refreshIfStale`. Two seconds keeps
   * us within roughly one or two watch ticks; 50 m of horizontal accuracy
   * is fine for centring the map and pin proximity. Tune per call site
   * via the optional arguments if a flow needs tighter precision.
   */
  private readonly STALE_AGE_MS = 2_000;
  private readonly STALE_ACCURACY_M = 50;

  private isFixFreshEnough(maxAgeMs: number, maxAccuracyM: number): boolean {
    const fix = this._currentLocation();
    if (!fix) return false;
    if (this.lastEmitAt === 0) return false;
    const ageMs = performance.now() - this.lastEmitAt;
    if (ageMs > maxAgeMs) return false;
    const accuracy = fix.accuracy;
    if (accuracy == null || accuracy > maxAccuracyM) return false;
    return true;
  }

  async refreshIfStale(
    maxAgeMs: number = this.STALE_AGE_MS,
    maxAccuracyM: number = this.STALE_ACCURACY_M,
  ): Promise<LocationResult> {
    const existing = this._currentLocation();
    if (this.isFixFreshEnough(maxAgeMs, maxAccuracyM) && existing) {
      return { success: true, coordinates: existing };
    }
    return this.refreshHighAccuracy();
  }

  async refreshHighAccuracy(): Promise<LocationResult> {
    if (!navigator.geolocation) {
      return { success: false, error: 'errors.gps_unavailable' };
    }
    if (this._permissionState() === 'denied') {
      return { success: false, error: 'errors.location_permission_denied' };
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords: LocationCoordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };
          this.lastEmitAt = performance.now();
          this.ngZone.run(() => {
            this._currentLocation.set(coords);
            this._locationError.set('');
            // A successful native fix means permission is effectively granted.
            // Lock that in so startTracking() can begin the watch even if the
            // Permissions API listener hasn't fired yet.
            this._permissionState.set('granted');
            this.clearDeniedState();
          });
          this.startTracking();
          resolve({ success: true, coordinates: coords });
        },
        () => {
          resolve({ success: false, error: 'map.location_error' });
        },
        { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 }
      );
    });
  }

  clearError(): void {
    this._locationError.set('');
  }

  async checkPermissionState(): Promise<PermissionState> {
    if (!navigator.permissions) {
      return 'unknown';
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      const state = result.state as PermissionState;
      this._permissionState.set(state);
      return state;
    } catch {
      return 'unknown';
    }
  }

  markAsExplicitlyDenied(): void {
    this._wasExplicitlyDenied.set(true);
    this._permissionState.set('denied');
  }

  clearDeniedState(): void {
    this._wasExplicitlyDenied.set(false);
  }

  onPermissionChange(callback: (state: PermissionState) => void): () => void {
    this.changeListeners.push(callback);
    return () => {
      this.changeListeners = this.changeListeners.filter(cb => cb !== callback);
    };
  }

  detectPlatform(): 'ios' | 'android' | 'macos' | 'windows' | 'other' {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    }
    if (/android/.test(userAgent)) {
      return 'android';
    }
    if (/macintosh|mac os x/.test(userAgent)) {
      return 'macos';
    }
    if (/windows/.test(userAgent)) {
      return 'windows';
    }
    return 'other';
  }

  getBrowserName(): 'chrome' | 'safari' | 'brave' | 'firefox' | 'edge' | 'samsung' | 'other' {
    const userAgent = navigator.userAgent;
    
    // Brave identifies itself as Chrome but has navigator.brave
    if ((navigator as any).brave?.isBrave) {
      return 'brave';
    }
    // Edge (Chromium-based)
    if (userAgent.includes('Edg/')) {
      return 'edge';
    }
    // Samsung Internet
    if (userAgent.includes('SamsungBrowser')) {
      return 'samsung';
    }
    // Firefox
    if (userAgent.includes('Firefox')) {
      return 'firefox';
    }
    // Chrome (must check after Brave/Edge/Samsung since they include "Chrome")
    if (userAgent.includes('Chrome')) {
      return 'chrome';
    }
    // Safari (must check last since Chrome also includes "Safari")
    if (userAgent.includes('Safari')) {
      return 'safari';
    }
    return 'other';
  }

  getBrowserDisplayName(): string {
    const browser = this.getBrowserName();
    const names: Record<string, string> = {
      chrome: 'Chrome',
      safari: 'Safari',
      brave: 'Brave',
      firefox: 'Firefox',
      edge: 'Edge',
      samsung: 'Samsung Internet',
      other: 'your browser'
    };
    return names[browser] || 'your browser';
  }

  private async initializePermissionListener(): Promise<void> {
    if (!navigator.permissions) {
      return;
    }

    try {
      this.permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
      this._permissionState.set(this.permissionStatus.state as PermissionState);

      if (this.permissionStatus.state === 'granted') {
        this.startTracking();
      }

      this.permissionStatus.onchange = () => {
        if (this.permissionStatus) {
          const newState = this.permissionStatus.state as PermissionState;
          
          this.ngZone.run(() => {
            this._permissionState.set(newState);
            
            if (newState === 'granted') {
              this._wasExplicitlyDenied.set(false);
              this._showDialog.set(false);
              this.requestLocation();
              this.startTracking();
            } else {
              this.stopTracking();
            }

            this.changeListeners.forEach(cb => cb(newState));
          });
        }
      };
    } catch {
      // Permission API not supported or geolocation query failed
    }
  }

  private cleanup(): void {
    if (this.permissionStatus) {
      this.permissionStatus.onchange = null;
      this.permissionStatus = null;
    }
    this.changeListeners = [];
  }
}
